import { useEffect, useMemo, useState } from "react";
import { WebsiteStat } from "../../../models/WebsiteModels";
import { resolveImageUrl } from "../../../utils/ImageUrl";
import "./WebsiteModal.css";

interface StatModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  item: WebsiteStat | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: StatFormValues) => Promise<void>;
}

export interface StatFormValues {
  value: string;
  label: string;
  iconFile: File | null;
}

const emptyForm: StatFormValues = {
  value: "",
  label: "",
  iconFile: null,
};

const STAT_ICON_MAX_BYTES = 100 * 1024;

const fromStat = (item: WebsiteStat): StatFormValues => ({
  value: item.value,
  label: item.label,
  iconFile: null,
});

const StatModal = ({
  isOpen,
  mode,
  item,
  submitting,
  onClose,
  onSubmit,
}: StatModalProps) => {
  const [form, setForm] = useState<StatFormValues>(emptyForm);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      setForm(emptyForm);
    } else if (item) {
      setForm(fromStat(item));
    }
    setFileError(null);
  }, [isOpen, mode, item]);

  const iconPreview = useMemo(() => {
    if (form.iconFile) return URL.createObjectURL(form.iconFile);
    const resolved = resolveImageUrl(item?.icon);
    return resolved && !resolved.startsWith("data:") ? resolved : undefined;
  }, [form.iconFile, item?.icon]);

  useEffect(() => {
    if (form.iconFile) {
      const url = URL.createObjectURL(form.iconFile);
      return () => URL.revokeObjectURL(url);
    }
  }, [form.iconFile]);

  if (!isOpen) return null;

  const setField = (key: keyof StatFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleIconFileChange = (file: File | null) => {
    setForm((prev) => ({ ...prev, iconFile: file }));
    if (file && file.size > STAT_ICON_MAX_BYTES) {
      setFileError(
        `Icon image must be 100 KB or less (selected file is ${Math.round(file.size / 1024)} KB).`,
      );
    } else {
      setFileError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.iconFile && form.iconFile.size > STAT_ICON_MAX_BYTES) {
      setFileError(
        `Icon image must be 100 KB or less (selected file is ${Math.round(form.iconFile.size / 1024)} KB).`,
      );
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="wbm-overlay">
      <div className="wbm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wbm-modal__header">
          <h3>{mode === "create" ? "Add Stat" : "Edit Stat"}</h3>
          <button
            className="wbm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="wbm-modal__body" onSubmit={handleSubmit}>
          <div className="wbm-form-group">
            <label htmlFor="wbs-value">Value *</label>
            <input
              id="wbs-value"
              type="text"
              placeholder="e.g. 12+"
              value={form.value}
              onChange={(e) => setField("value", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbs-label">Label *</label>
            <input
              id="wbs-label"
              type="text"
              placeholder="e.g. Years of experience"
              value={form.label}
              onChange={(e) => setField("label", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbs-icon-file">Icon image</label>
            {iconPreview && (
              <img
                className="wbm-preview"
                src={iconPreview}
                alt="Icon preview"
              />
            )}
            <input
              id="wbs-icon-file"
              type="file"
              accept="image/*"
              onChange={(e) => handleIconFileChange(e.target.files?.[0] ?? null)}
            />
            <p className="wbm-hint">Max 100 KB.</p>
            {fileError && <p className="wbm-error">{fileError}</p>}
          </div>

          <div className="wbm-modal__actions">
            <button
              type="button"
              className="wbm-btn wbm-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wbm-btn wbm-btn--primary"
              disabled={
                submitting || !!fileError || !form.value.trim() || !form.label.trim()
              }
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StatModal;